/**
 * Impersonation engine — permission model.
 *
 * A declarative "ACTOR X may impersonate TARGET kind Y" policy, injected into the
 * provider. This is a UX guardrail: it hides buttons (`canImpersonate`) and
 * short-circuits obviously-disallowed `impersonate()` calls.
 *
 * ── What is an "actor", and why do we need it? ──────────────────────────────
 * The "actor" is WHO is doing the impersonating. Every permission rule is a pair:
 * "actor → may impersonate → kind". You can't decide if an impersonation is
 * allowed without knowing who's asking:
 *   - an `advisor` impersonating a client (`hoh`)  → fine
 *   - an `hoh`     impersonating anyone            → not fine
 * Same target, different answer — the difference is the actor. So the policy is
 * keyed by actor, and we must resolve "who is the actor right now" before each
 * check (see `resolveActor`).
 *
 * The twist — drill-down: once you're already impersonating, the actor is NOT
 * your login role, it's the role you're CURRENTLY "wearing" (top of the stack).
 *   Example: a Reviewer impersonates an `advisor` (allowed — reviewer is "*").
 *   Now you're acting AS that advisor. From inside, you try to impersonate
 *   another `advisor`. The question becomes "can an ADVISOR impersonate an
 *   advisor?" → no (an advisor may only impersonate `hoh`), NOT "can a reviewer?"
 *   (which is "*" and would allow it). You only get the powers of the role you're
 *   acting as — which mirrors what the backend should enforce per hop.
 *   (Under DEFAULT_POLICY `firm_admin` and `enterprise_admin` can impersonate
 *   nothing, so wearing either of those roles ends the chain.)
 * ────────────────────────────────────────────────────────────────────────────
 */
import { ROLE_CHOICES, RoleType } from "@/shared/lib/types";
import type {
  ImpersonationKind,
  ImpersonationState,
  ImpersonationTarget,
} from "./types";

/**
 * Who can be doing the impersonating. It's the union of TWO groups:
 *
 *   1. Every `ImpersonationKind` — because of drill-down: once you impersonate a
 *      role, that role becomes the actor for the next hop. So a kind like
 *      `ria` must also be expressible as an actor.
 *   2. Actor-only roles (`reviewer`, `advisory_ops`, `reporter`) — real app roles
 *      that can ACT but are never themselves a TARGET (there is no "be a reviewer"
 *      route/headers to impersonate into).
 *
 * All values are `ROLE_CHOICES` strings (matching `ImpersonationKind`), so actors
 * and kinds share one vocabulary. `ActorRole` is still wider than
 * `ImpersonationKind` because of the actor-only roles.
 */
export type ActorRole =
  | ImpersonationKind
  | (typeof ROLE_CHOICES)[RoleType.Reviewer]
  | (typeof ROLE_CHOICES)[RoleType.AdvisoryOps]
  | (typeof ROLE_CHOICES)[RoleType.Reporter];

/**
 * For each actor, the kinds they may impersonate.
 * - `"*"`           => all kinds (today's reviewer behavior).
 * - `ImpersonationKind[]` => exactly those kinds.
 * - missing key / empty array => actor cannot impersonate anything.
 */
export type ImpersonationPolicy = Partial<
  Record<ActorRole, ImpersonationKind[] | "*">
>;

/**
 * Actors with no entry (head_of_the_family, family_member, corporate_admin,
 * self_directed, advisory_ops, reporter) cannot impersonate. `ria` and
 * `enterprise_admin` have explicit empty entries — also blocked, until Phase 2
 * fills in their real allowances.
 */
export const DEFAULT_POLICY: ImpersonationPolicy = {
  [ROLE_CHOICES[RoleType.Reviewer]]: "*",
  [ROLE_CHOICES[RoleType.EnterpriseAdmin]]: [],
  [ROLE_CHOICES[RoleType.RIA]]: [],
  [ROLE_CHOICES[RoleType.FinancialAdvisor]]: [
    ROLE_CHOICES[RoleType.HeadOfTheFamily],
  ],
};

/** Payload passed to the provider's optional `onPermissionDenied` callback. */
export type PermissionDeniedPayload = {
  actor: ActorRole;
  target: ImpersonationTarget;
};

/**
 * Pure check: may `actor` impersonate `targetKind` under `policy`?
 *
 * Examples against DEFAULT_POLICY:
 *   isImpersonationAllowed("reviewer", "financial_advisor")          → true  (reviewer = "*")
 *   isImpersonationAllowed("financial_advisor", "head_of_the_family") → true (→ ["head_of_the_family"])
 *   isImpersonationAllowed("financial_advisor", "financial_advisor") → false (not in list)
 *   isImpersonationAllowed("ria", "financial_advisor")               → false (ria → [])
 *   isImpersonationAllowed("head_of_the_family", "family_member")    → false (no entry)
 */
export const isImpersonationAllowed = (
  actor: ActorRole,
  targetKind: ImpersonationKind,
  policy: ImpersonationPolicy = DEFAULT_POLICY,
): boolean => {
  const allowed = policy[actor];
  if (!allowed) return false;
  if (allowed === "*") return true;
  return allowed.includes(targetKind);
};

/** Every role that can ACT as an impersonator (a `ROLE_CHOICES` string). */
const ACTOR_ROLES: ReadonlySet<string> = new Set([
  ROLE_CHOICES[RoleType.FamilyMember],
  ROLE_CHOICES[RoleType.HeadOfTheFamily],
  ROLE_CHOICES[RoleType.FinancialAdvisor],
  ROLE_CHOICES[RoleType.RIA],
  ROLE_CHOICES[RoleType.EnterpriseAdmin],
  ROLE_CHOICES[RoleType.CorporateAdmin],
  ROLE_CHOICES[RoleType.SelfDirected],
  ROLE_CHOICES[RoleType.Reviewer],
  ROLE_CHOICES[RoleType.AdvisoryOps],
]);

/**
 * Maps a logged-in user's stored role (a `ROLE_CHOICES` string from
 * `@/shared/lib/types`, e.g. set by `useRoleDeterminer`) to an `ActorRole`.
 *
 * Now that `ActorRole` IS the `ROLE_CHOICES` vocabulary, this is just a validated
 * pass-through: a recognized role maps to itself, and unknown / null roles fail
 * CLOSED to `"reporter"` (which has no policy entry) so we never grant
 * impersonation by accident.
 */
export const mapUserRoleToActorRole = (
  role: string | null | undefined,
): ActorRole =>
  role && ACTOR_ROLES.has(role)
    ? (role as ActorRole)
    : ROLE_CHOICES[RoleType.Reporter];

/**
 * Resolves "who is the actor right now". Callers never pass this — the engine
 * derives it:
 * - While impersonating, the actor is whatever role sits on TOP of the stack
 *   (the role you're currently "wearing").
 * - Otherwise the actor is the logged-in user's real role (mapped from the app's
 *   ROLE_CHOICES string into our ActorRole vocabulary).
 *
 * Examples:
 *   resolveActor(null, "financial_advisor")                   → "financial_advisor"
 *     (not impersonating yet → use login role)
 *   resolveActor({ stack: [riaFrame], ... }, "reviewer")      → "ria"
 *     (drill-down: a reviewer acting as ria → next hop is checked as ria, NOT
 *      reviewer)
 */
export const resolveActor = (
  state: ImpersonationState,
  userRole: string | null | undefined,
): ActorRole => {
  if (state) return state.stack[state.stack.length - 1].kind;
  return mapUserRoleToActorRole(userRole);
};
